import React, { useContext, useEffect, useState } from 'react';
import { Card, Color, Text, Alert, Button } from '@bb/elements-web';
import styled from 'styled-components';
import Big from 'big.js';
import AnsweringServiceContext from '../../AnsweringServiceContext';

/**
 * The interactive card for the user to get a price estimate of subscribing
 * to Live Concierge Service. Should only be visible at an org/brokerage viewing level.
 */
const PriceEstimatorCard = () => {
  const { billingDetails } = useContext(AnsweringServiceContext);
  const [agentCount, setAgentCount] = useState<string | number>(
    billingDetails.billableAgentCount || 1,
  );
  const [totalPrice, setTotalPrice] = useState<string>(billingDetails.rate);

  const resetAgentCount = () => {
    if (billingDetails && billingDetails.billableAgentCount) {
      setAgentCount(billingDetails.billableAgentCount);
    }
  };

  /** Function for saving the entered number only if it valid */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.trim();
    setAgentCount(input);
  };

  useEffect(() => {
    // eslint-disable-next-line no-restricted-globals
    if (agentCount === '' || isNaN(Number(agentCount))) {
      setTotalPrice('0.00');
    } else {
      const estimatedTotalPrice = Big(billingDetails.rate)
        .times(agentCount)
        .toFixed(2);

      setTotalPrice(estimatedTotalPrice);
    }
  }, [agentCount, billingDetails.rate]);

  return (
    <StyledCard title="Price Estimator" dataCy="price-estimator-card">
      <Label htmlFor="lcs-agent-count-input">Number of Active Agents</Label>
      <StyledInput
        id="lcs-agent-count-input"
        type="number"
        min={0}
        max={99999}
        step={1}
        value={agentCount}
        onChange={handleInputChange}
      />
      <StyledButton
        dataCy="lcs-reset-agent-count-btn"
        type="secondary"
        onClick={resetAgentCount}
        styleOverride={{ border: 'none', padding: 0 }}
      >
        Select my current total # of agents
      </StyledButton>
      <Alert
        type="info"
        message={
          <>
            <Text size="large" style={{ marginBottom: '8px' }}>
              Your monthly price
            </Text>
            <PriceRow>
              <PriceAmount>${totalPrice}</PriceAmount>
              <PricePeriod> / month</PricePeriod>
            </PriceRow>
          </>
        }
        style={{ padding: 16 }}
      />
    </StyledCard>
  );
};

export default PriceEstimatorCard;

export const StyledCard = styled(Card)`
  width: 313px;

  .ant5-card-head-title {
    font-weight: 700;
    font-size: 18px;
  }
`;

const Label = styled.label`
  display: block;
  font-weight: 400;
  margin-bottom: 8px;
  color: ${Color.BODY_TEXT};
  font-size: 16px;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${Color.BORDER_GREY};
  border-radius: 4px;
  font-size: 16px;

  &:focus {
    outline: none;
    border-color: ${Color.PRIMARY};
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
`;

const StyledButton = styled(Button)`
  color: ${Color.PRIMARY};
  margin: 8px 0 16px 0;
  font-size: 16px;
  font-weight: 400;
  cursor: pointer;
  line-height: 22px;

  &:hover {
    text-decoration: underline;
  }
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
`;

const PriceAmount = styled.span`
  font-size: 28px;
  font-weight: 700;
`;

const PricePeriod = styled.span`
  font-weight: 400;
  margin-bottom: 8px;
  color: ${Color.BODY_TEXT};
  font-size: 16px;
  line-height: 22px;
`;
